/* eslint-disable react-refresh/only-export-components */

import { createContext, useContext, useEffect, useState } from 'react';

import { usePersistentState } from '@crossnokaye/ux-common';

import { LocalStorageKeys } from '@/constants';

type Theme = 'dark' | 'light' | 'system';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: 'dark',
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export const ThemeProvider = ({
  children,
  defaultTheme = 'dark',
  storageKey = LocalStorageKeys.ViteUITheme,
  ...props
}: ThemeProviderProps) => {
  const [value, setValue] = usePersistentState<string>(localStorage, storageKey);

  const [theme, setTheme] = useState<Theme>((value as Theme) ?? defaultTheme);

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove('light', 'dark');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';

      root.classList.add(systemTheme);
      return;
    }

    root.classList.add(theme);
  }, [theme]);

  const themeValue = {
    theme,
    setTheme: async (theme: Theme) => {
      await setValue(theme as string);
      setTheme(theme);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={themeValue}>
      {children}
    </ThemeProviderContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined) throw new Error('useTheme must be used within a ThemeProvider');

  return context;
};
